import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, map, Observable, of, Subject, take, takeUntil } from "rxjs";
import { selectFrequency, selectInputIraFields, selectInternalTransfersInfo, selectIs401kToAccount, selectIsFromEdit, selectIsOneTimeTotalDist, selectITDistributionReason, selectItIsIdentical, selectToAccountsInfo, selectTransactionForm } from "../../reducers";
import { IraFields } from "../../models/internal-transfer/IraFields";
import { FormArray, UntypedFormBuilder, FormGroup } from "@angular/forms";
import { AmountType } from "../../../shared/types/Transaction";
import { ToAccountInfoById } from "../../models/internal-transfer/InternalTransfersInfo";
import { MultipleToAccount } from "../../models/internal-transfer/MultipleToAccount";
import { selectIsIra } from "../../../account-info-header/reducers";
import { ItCashAmounts } from "../../models/form/TransactionForm";
import { amountTypeDict } from "../../../shared/utilities/Constants";

@Component({
  standalone: false,
  selector: 'mm-it-multiple-cash-inputs',
  templateUrl: './it-multiple-cash-inputs.component.html',
  styleUrls: ['./it-multiple-cash-inputs.component.scss'],
})
export class ItMultipleCashInputsComponent implements OnInit, OnDestroy {
  @Input() autoCalculateRmd$: Observable<boolean>;
  @Input() parentForm: FormGroup;
  @Input() amountTypeSelection: AmountType;
  @Input() amountType : ItCashAmounts[];
  iraFields: IraFields = new IraFields();
  isIraTransaction: boolean;
  toAccountList: MultipleToAccount[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  frequency$: Observable<"onetime" | "onetimeTotalDist" | "recurring" | undefined>= of("onetime");
  isOneTimeTotalDist: boolean;
  isEdit: boolean = false;
  distributionReasonDescriptionArr$: Observable<string>[] = [];
  isFirstPartyIraToIraJournalArr$: Observable<boolean>[] = [];
  constructor(
    private fb: UntypedFormBuilder,
    private store: Store
    ) {}

  ngOnInit(): void {
    this.parentForm.setControl('cashAmounts', this.fb.array([]));

    combineLatest([
      this.store.select(selectIsFromEdit),
      this.store.select(selectToAccountsInfo),
      this.store.select(selectInternalTransfersInfo),
      this.store.select(selectIs401kToAccount)
    ]).pipe(take(1)).subscribe(([isFromEdit, toAccounts, verifyRequest, is401kToAccount]) => {
      this.isEdit = isFromEdit;
      if (isFromEdit) {
        const keys = Object.keys(toAccounts);
        var journalAmountsById : {[accountId: string]: string} = {};
          
        keys.forEach(toAccount => {
          var toAccountRequest = verifyRequest.multipleToJournalsList.find(request => request.toAccountInfo.accountNumber === toAccount);
          var index = this.amountType.findIndex(request => request.toAccount === toAccount);       
          if (toAccountRequest){
            journalAmountsById = {
              ...journalAmountsById,
              [toAccount] : toAccountRequest?.iraFields?.isIraTransaction ? this.amountType[index]?.amount : toAccountRequest.journalCashAmount
            }
          }
          if (is401kToAccount){
            journalAmountsById = {
              ...journalAmountsById,
              [toAccount] : verifyRequest?.iraFields?.[toAccount].isIraTransaction ? this.amountType[index]?.amount ?? '' : verifyRequest?.toAccountPropertiesById[toAccount]?.journalCashAmount ?? ''
            }
          }
        });
        this.obtainToAccountInfo(toAccounts, journalAmountsById);
      }
    });

    this.store.select(selectIsIra).pipe(takeUntil(this.destroy$)).subscribe(isIra => {
      this.isIraTransaction = isIra;
    });

    this.frequency$ = this.store.select(selectFrequency);

    this.store.select(selectIsOneTimeTotalDist).pipe(takeUntil(this.destroy$)).subscribe(isOneTimeTotalDist => {
      this.isOneTimeTotalDist = isOneTimeTotalDist;
    });
    
    this.store.select(selectInputIraFields).pipe(takeUntil(this.destroy$)).subscribe(inputIraFields => {
      this.iraFields = inputIraFields;
    });

    this.store.select(selectToAccountsInfo).pipe(takeUntil(this.destroy$)).subscribe(toAccounts => {
      const keys = Object.keys(toAccounts);
      if (keys.length > 0) {
        this.obtainToAccountInfo(toAccounts);
      }
      this.removeToAccountInfo(keys);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getDistributionReasonDescription$(toAccountIndex: number): Observable<string> {
    return this.store.select(selectTransactionForm).pipe(
      takeUntil(this.destroy$),
      map((transactionForm) => {
        if ("toAccounts" in transactionForm?.transactionDetails) {
          return transactionForm.transactionDetails.toAccounts?.[toAccountIndex]?.iraDistributionType ?? '';
        }
        return '';
      })
    );
  }

  getIsFirstPartyIraToIraJournal$(toAccountIndex: number): Observable<boolean> {
    return combineLatest([
      this.store.select(selectIsIra),
      of(this.toAccountList[toAccountIndex]?.isIraAccount),
      of(this.toAccountList[toAccountIndex]?.isIdentical)
    ]).pipe(
      map(([isIra, isToAccountIra, isFirstPartyAccount]) =>  !!isIra && !!isToAccountIra && !!isFirstPartyAccount)
    );
  }

  get toAccounts() {
    return this.parentForm.get('cashAmounts') as FormArray;
  }

  getToAccount(toAccountIndex: number) {
    return this.toAccounts.at(toAccountIndex) as FormGroup;
  }

  gettoAccountInfo(toAccount: MultipleToAccount) {
    return `${toAccount.name} - ${toAccount.accountType}`;
  }
  getAmountTypeSelection(toAccountIndex: number) : AmountType {
    this.amountTypeSelection= amountTypeDict.gross;
    if (this.isEdit &&  this.amountType[toAccountIndex]?.amountType) {
      this.amountTypeSelection= this.amountType[toAccountIndex]?.amountType as AmountType;
      return this.amountTypeSelection;
    }
    return this.amountTypeSelection;
  }

  obtainToAccountInfo(toAccounts: ToAccountInfoById, journalAmountsById: {[accountId: string]: string} = {}) {
    const keys = Object.keys(toAccounts);
    keys?.forEach((key) => {
      if (!this.toAccounts?.controls?.find(control => control.value.toAccount === key)) {
        let toAccount: MultipleToAccount = {
          name: toAccounts[key].accountRegistration[0],
          accountType: toAccounts[key].regTypeShortName,
          accountNumber: toAccounts[key].accountNumber,
          amountLabelName: `${toAccounts[key].accountRegistration[0] ?? toAccounts[key].accountNumber} - ${toAccounts[key].regTypeShortName}`,
          isIraAccount: toAccounts[key].isIraAccount,
          isIdentical: toAccounts[key].isIdentical ?? false //determines if to account is 1st or 3rd Party
        };
        this.toAccountList.push(toAccount);
        this.toAccounts.push(
          this.newToAccount(toAccount.accountNumber, journalAmountsById[toAccount.accountNumber])
        );
        this.distributionReasonDescriptionArr$ = this.toAccountList.map((_, i) => this.getDistributionReasonDescription$(i));
        this.isFirstPartyIraToIraJournalArr$ = this.toAccountList.map((_, i) => this.getIsFirstPartyIraToIraJournal$(i));
      }
    });
  }

  removeToAccountInfo(keys: string[]) {
    this.toAccounts?.controls?.forEach((control, index) => {
      if(!keys?.find((key) => key === control.value.toAccount)) {
        const accountIndex = this.toAccountList.findIndex(toAccount => toAccount.accountNumber === control.value.toAccount);
        if (accountIndex >= 0) {
          this.toAccountList.splice(accountIndex, 1);
        }
        this.toAccounts.removeAt(index);
      }
    })
  }

  newToAccount(toAccount: string, cashAmount: string = ""): FormGroup {
    return this.fb.group({ toAccount: toAccount, amount: cashAmount });
  }
}
